import { model as i18n } from '!/i18n'
import { model as router } from '!/router'
import { formatSecondsToDaysOrHoursOrMinutes } from '@/helpers'
import { api } from '@setplex/tria-api'
import {
  combine,
  createEvent,
  createStore,
  sample,
  split,
  type Store,
} from 'effector'
import { interval } from 'patronum'
import { formatDateDuration } from '~/shared/helpers/date'
import type { AllReadyTranslatedReturned, UpdateTimeTextProps } from './index.h'

/*** Start shared logic for countdown seconds in errors ***/
const TIMER_DELAY = 1000

export const updateTextError = createEvent<UpdateTimeTextProps>()
export const startTimerError = createEvent<number>()
export const stopTimerError = createEvent<void>()
export const updateTickSeconds = createEvent<number>()

const { tick } = interval({
  timeout: TIMER_DELAY,
  start: startTimerError,
  stop: stopTimerError,
})

const updateLongTimeError = createEvent<UpdateTimeTextProps>()
const $textLongTimeError = createStore<string>('') //
  .on(updateLongTimeError, (_, { translated }) => translated || '')
const $tickTimer = createStore<number>(0).on(tick, (n) => n - 1)

sample({
  clock: [
    api.events.logic.noMoreVerifyAttempts,
    api.events.logic.noMoreAttemptsOnePhoneNumber,
    api.events.logic.noMoreAttemptsOneIp,
  ],
  filter: ({ payload }) => {
    const remainsSeconds = payload?.remainsSeconds
    return Boolean(remainsSeconds)
  },
  fn: ({ payload }) => {
    const { remainsSeconds } = payload as { remainsSeconds: number }
    return remainsSeconds
  },
  target: [startTimerError, $tickTimer],
})

export const $lastErrorTranslationKey = createStore<string | null>(null)
  .on(
    api.events.logic.noMoreVerifyAttempts,
    () => 'invalid_sms_code_attempt_with_time_error'
  )
  .on(
    [
      api.events.logic.noMoreAttemptsOnePhoneNumber,
      api.events.logic.noMoreAttemptsOneIp,
    ],
    () => 'qr_requests_attempt_error'
  )

export const $allReadyTranslated: Store<AllReadyTranslatedReturned> = combine(
  { key: $lastErrorTranslationKey, tick: $tickTimer, i18n: i18n.$t },
  ({ key, tick, i18n: { t } }) => {
    const time = formatDateDuration({
      duration: {
        seconds: tick,
      },
    })
    return key
      ? { translated: t(key, { time }), key }
      : { translated: '', key: '' }
  }
)

sample({
  clock: $tickTimer,
  filter: (payload) => {
    const { unit } = formatSecondsToDaysOrHoursOrMinutes(payload)
    return unit === 'seconds'
  },
  target: updateTickSeconds,
})

split({
  clock: updateTickSeconds,
  source: $allReadyTranslated,
  match: {
    noMoreVerifyAttempts: ({ key }) =>
      key === 'invalid_sms_code_attempt_with_time_error',
    noMoreAttempts: ({ key }) => key === 'qr_requests_attempt_error',
  },
  cases: {
    noMoreVerifyAttempts: updateTextError,
    noMoreAttempts: updateLongTimeError,
  },
})

sample({
  clock: [
    api.events.logic.noMoreAttemptsOnePhoneNumber,
    api.events.logic.noMoreAttemptsOneIp,
  ],
  source: i18n.$t,
  fn: ({ t }, { payload }) => {
    const time = formatDateDuration({
      duration: {
        seconds: payload?.remainsSeconds,
      },
    })
    return t('qr_requests_attempt_error', { time })
  },
  target: $textLongTimeError,
})

// redirect on fail form for errors with long waiting time
sample({
  clock: [
    api.events.logic.noMoreAttemptsOnePhoneNumber,
    api.events.logic.noMoreAttemptsOneIp,
  ],
  source: $textLongTimeError,
  fn: (state) => ({
    pathname: '/fail',
    state,
  }),
  target: router.navigatePush,
})

// remove Error Message after the end of the error time
sample({
  clock: $tickTimer,
  filter: (tick) => tick < 1,
  fn: () => ({
    translated: null,
    key: null,
  }),
  target: updateTextError,
})
/*** End shared logic for countdown seconds in errors ***/
